// ---------------------------------------------------------------
// shiftNoteService.js — Notas del equipo entre turnos.
//
// "Falta stock de Ibuprofeno 600, pedir mañana", "la Sra. Gómez
// recoge la receta a partir de las 17:00". Cosas que quien sale del
// turno necesita contarle a quien entra.
//
// TODA NOTA CADUCA, y esto no es opcional: al escribirla hay que
// decir hasta cuándo sirve. Sin eso el tablón se llena de avisos de
// hace tres meses y deja de leerse, que es la forma más segura de
// que un aviso importante pase desapercibido.
//
// Las caducadas no se ocultan: directamente NO SE DESCARGAN (la
// consulta filtra por fecha), así que no gastan ni lecturas. Aparte,
// se pueden barrer del todo con `purgeNotasCaducadas`.
// ---------------------------------------------------------------
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { sharedSubscribe } from "../utils/sharedSnapshot";

/** Cuánto puede durar una nota como máximo (las reglas lo obligan) */
export const MAX_DIAS_NOTA = 30;

/**
 * Opciones rápidas de caducidad. Se calculan al abrirlas, no al
 * cargar el módulo, para que "mañana" siga siendo mañana aunque la
 * pestaña lleve horas abierta.
 */
export function opcionesCaducidad(ahora = new Date()) {
  const a = (fecha, h, m = 0) => {
    const d = new Date(fecha);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const manana = new Date(ahora);
  manana.setDate(manana.getDate() + 1);
  const enDias = (n) => {
    const d = new Date(ahora);
    d.setDate(d.getDate() + n);
    return a(d, 21, 0);
  };

  return [
    { clave: "cierre", etiqueta: "Al cerrar hoy", fecha: a(ahora, 21, 30) },
    { clave: "mananaAbrir", etiqueta: "Mañana al abrir", fecha: a(manana, 10, 0) },
    { clave: "mananaCierre", etiqueta: "Mañana al cerrar", fecha: a(manana, 21, 30) },
    { clave: "tresDias", etiqueta: "En 3 días", fecha: enDias(3) },
    { clave: "semana", etiqueta: "En una semana", fecha: enDias(7) },
    // La última no trae fecha: la pone quien escribe, día y hora
    { clave: "otra", etiqueta: "Otra fecha…", fecha: null },
  ];
}

/** Lo más tarde que puede caducar una nota (las reglas cortan en 31 días) */
export function maximoCaducidad(ahora = new Date()) {
  const d = new Date(ahora);
  d.setDate(d.getDate() + MAX_DIAS_NOTA);
  return d;
}

/** "2026-08-09T10:00" para un <input type="datetime-local"> */
export function paraInputLocal(fecha) {
  const d = new Date(fecha);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** "mañana a las 10:00", "hoy a las 21:30", "el 14 de agosto a las 21:00" */
export function textoCaducidad(fecha) {
  if (!fecha) return "";
  const d = fecha instanceof Date ? fecha : new Date(fecha.seconds * 1000);
  const hoy = new Date();
  const mismoDia = (a, b) => a.toDateString() === b.toDateString();
  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);

  const hora = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  if (mismoDia(d, hoy)) return `hoy a las ${hora}`;
  if (mismoDia(d, manana)) return `mañana a las ${hora}`;
  return `el ${d.toLocaleDateString("es-ES", { day: "numeric", month: "long" })} a las ${hora}`;
}

/**
 * Notas VIGENTES, de la más reciente a la más antigua.
 * El `where` sobre `expiresAt` es lo que hace que las caducadas ni
 * lleguen al navegador.
 */
export function subscribeNotasTurno(onData, onError, max = 20) {
  return sharedSubscribe(
    `shiftNotes:${max}`,
    (cb, err) =>
      onSnapshot(
        query(
          collection(db, "shiftNotes"),
          where("expiresAt", ">", new Date()),
          orderBy("expiresAt", "asc"),
          limit(max)
        ),
        (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        err
      ),
    onData,
    onError
  );
}

export function crearNotaTurno({ texto, expiresAt, uid, nombre }) {
  const limpio = String(texto || "").trim();
  if (!limpio || !expiresAt || !uid) return Promise.resolve(null);
  return addDoc(collection(db, "shiftNotes"), {
    text: limpio.slice(0, 500),
    expiresAt,
    authorUid: uid,
    authorName: nombre || "",
    createdAt: serverTimestamp(),
  });
}

export function borrarNotaTurno(id) {
  return deleteDoc(doc(db, "shiftNotes", id));
}

/**
 * Barre las notas ya caducadas. Cualquiera puede hacerlo: son avisos
 * que ya no sirven y así no hace falta esperar a que pase un admin.
 */
export async function purgeNotasCaducadas() {
  const snap = await getDocs(
    query(collection(db, "shiftNotes"), where("expiresAt", "<=", new Date()))
  );
  if (snap.empty) return 0;
  for (let i = 0; i < snap.docs.length; i += 450) {
    const lote = writeBatch(db);
    for (const d of snap.docs.slice(i, i + 450)) lote.delete(d.ref);
    await lote.commit();
  }
  return snap.docs.length;
}
